import { useSelector } from "react-redux";

import {
  DeploymentPermissions,
  SaleLotPermissions,
  SaleyardPermissions,
} from "constants/permissions";

import { hasPermission, someHasPermission } from "lib/permissions";

import {
  getActiveLivestockAgentDeployment,
  getActiveSaleyardAdminSaleyard,
  getCurrentSaleyard,
  getCurrentSale,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  selectCurrentSaleyard,
  selectRoleCurrentDeployments,
  selectRoleDeployments,
  getSaleLots,
} from "selectors";

/**
 * Tests whether the Model instance returned by the objectSelector contains the permission specified by the permissionRequired argument
 * @param {function(state: State): SelectedState} objectSelector
 * @param {String} permissionRequired
 * @template State, SelectedState
 * @returns {boolean}
 */
export function useHasPermission(objectSelector, permissionRequired) {
  return useSelector(state =>
    hasPermission(objectSelector(state), permissionRequired),
  );
}

/**
 * Tests whether any Model instance in the array returned by the objectSelector argument contains the permission specified by the permissionRequired argument
 * @param {function(state: State): SelectedState[]} objectSelector
 * @param {String} permissionRequired
 * @template State, SelectedState
 * @returns {boolean}
 */
export function useSomeHasPermission(objectSelector, permissionRequired) {
  return useSelector(state =>
    someHasPermission(objectSelector(state), permissionRequired),
  );
}

/**
 * Tests whether all Model instances in a lookup returned lookupSelector argument contains the permission specified by the permissionRequired argument
 * @param {function(state: State): SelectedState[]} lookupSelector
 * @param {String} permissionRequired
 * @param {function(obj): Boolean} filterFn
 * @template State, SelectedState
 * @returns {boolean}
 */
const defaultFilterFn = () => true;

export function useAllHasPermission(
  lookupSelector,
  permissionRequired,
  filterFn = defaultFilterFn,
) {
  return useSelector(state =>
    Object.values(lookupSelector(state))
      .filter(filterFn)
      .every(obj => hasPermission(obj, permissionRequired)),
  );
}

// Common usages

export function useHasDeploymentPermission(deploymentPermission) {
  return useHasPermission(
    getActiveLivestockAgentDeployment,
    deploymentPermission,
  );
}

export function useHasLivestockSalePermission(salePermission) {
  return useHasPermission(getCurrentSale, salePermission);
}

export function useHasSaleyardPermission(saleyardPermission) {
  return useHasPermission(selectCurrentSaleyard, saleyardPermission);
}

export function useHasSaleyardAdminSaleyardPermission(saleyardPermission) {
  return useHasPermission(getActiveSaleyardAdminSaleyard, saleyardPermission);
}

export function useHasSaleyardOrDeploymentPermission(
  deploymentPermission,
  saleyardPermission,
) {
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const selectedPermission = isLivestockAgent
    ? deploymentPermission
    : saleyardPermission;

  const parentObjectSelector = isLivestockAgent
    ? getActiveLivestockAgentDeployment
    : getActiveSaleyardAdminSaleyard;

  return useHasPermission(parentObjectSelector, selectedPermission);
}

export function useHasAddConsignmentPermission() {
  return useSelector(state =>
    someHasPermission(
      selectRoleCurrentDeployments(state),
      DeploymentPermissions.canAddConsignment,
    ),
  );
}

export function useHasEmailListPermission() {
  return useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureEmailList,
    SaleyardPermissions.featureEmailList,
  );
}

export function useHasReceivalOrPenScanLotsPermission() {
  const hasReceivalLotPermission = useHasSaleyardPermission(
    SaleyardPermissions.featureReceivalLots,
  );
  const hasPenScanPermission = useHasSaleyardPermission(
    SaleyardPermissions.featurePenScanLots,
  );
  return hasReceivalLotPermission || hasPenScanPermission;
}

export function useHasMultiSaleReportPermission() {
  return useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureMultiSaleReports,
    SaleyardPermissions.featureMultiSaleReports,
  );
}

export function useHasEditWeightOnEIDPermission() {
  return useHasSaleyardPermission(SaleyardPermissions.featureEditWeightOnEid);
}

export const useHasAddAttachmentWithoutImagePermission = () => {
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);
  const isLivestockAgent = useSelector(getIsLivestockAgent);

  let hasAddAttachmentWithoutImagePermission = false;

  const hasSaleyardAddAttachmentWithoutImagePermission =
    useHasSaleyardPermission(SaleyardPermissions.canAddAttachmentWithoutImage);
  const hasDeploymentAddAttachmentWithoutImagePermission =
    useHasDeploymentPermission(
      DeploymentPermissions.canAddAttachmentWithoutImage,
    );

  if (isSaleyardAdmin) {
    hasAddAttachmentWithoutImagePermission =
      hasSaleyardAddAttachmentWithoutImagePermission;
  }

  if (isLivestockAgent) {
    hasAddAttachmentWithoutImagePermission =
      hasDeploymentAddAttachmentWithoutImagePermission;
  }

  return hasAddAttachmentWithoutImagePermission;
};

export const useHasExportBusinessesPermission = () => {
  const saleyardHasExportBusinessesPermission = useHasPermission(
    getCurrentSaleyard,
    SaleyardPermissions.canExportBusinesses,
  );

  const deploymentHasExportBusinessesPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermissions.canExportBusinesses,
  );

  return (
    saleyardHasExportBusinessesPermission ||
    deploymentHasExportBusinessesPermission
  );
};

export const useHasSaleLotPermission = () =>
  useSomeHasPermission(
    state => Object.values(getSaleLots(state)),
    SaleLotPermissions.update,
  );

export const useHasMLAPermission = () =>
  useHasSaleyardPermission(SaleyardPermissions.featureMLASupplementaryData);

export const useHasDataIndicatorsPermission = () =>
  useHasSaleyardPermission(SaleyardPermissions.featureDataIndicators);

export const useHasMLAOrDataPermission = () => {
  const hasMLAPermission = useHasMLAPermission();
  const hasDataIndicatorsPermission = useHasDataIndicatorsPermission();
  return hasMLAPermission || hasDataIndicatorsPermission;
};

export const useHasNASPermissions = () => {
  const dentitonPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureDentition,
    SaleyardPermissions.featureDentition,
  );
  const weightRangePermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureWeightRange,
    SaleyardPermissions.featureWeightRange,
  );

  return dentitonPermission && weightRangePermission;
};

export const useHasManualChargesPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureManualCharges,
    SaleyardPermissions.featureManualCharges,
  );

export const useHasBillingPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureBilling,
    SaleyardPermissions.featureBilling,
  );

export const useHasBillingRelatedPermissions = () => {
  const hasBillingPermission = useHasBillingPermission();
  const hasManualChargesPermission = useHasManualChargesPermission();
  return hasBillingPermission || hasManualChargesPermission;
};

export const useHasBillingRunPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureBillingRun,
    SaleyardPermissions.featureBillingRun,
  );

export const useHasExternalIntegrationsPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureExternalIntegrations,
    SaleyardPermissions.featureExternalIntegrations,
  );

export const useCanLinkExternalIntegrationPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.canLinkExternalIntegration,
    SaleyardPermissions.canLinkExternalIntegration,
  );

export const useCanCreateLedgerAccountPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.canCreateLedgerAccount,
    SaleyardPermissions.canCreateLedgerAccount,
  );

export const useCanAddBillingRunPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.canAddBillingRun,
    SaleyardPermissions.canAddBillingRun,
  );

export const useCanAddSaleStatusPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.canAddSaleStatus,
    SaleyardPermissions.canAddSaleStatus,
  );

export const useHasTradingTermsPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureTradingTerms,
    SaleyardPermissions.featureTradingTerms,
  );

export const useHasRetainedProceedsPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureRetainedProceeds,
    SaleyardPermissions.featureRetainedProceeds,
  );

export const useHasXeroIntegrationPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureXeroIntegration,
    SaleyardPermissions.featureXeroIntegration,
  );

export const useHasBuyerSplitPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureBuyerSplits,
    SaleyardPermissions.featureBuyerSplits,
  );

export const useHasVendorSplitPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureVendorSplits,
    SaleyardPermissions.featureVendorSplits,
  );

// SaleyardAdmins don't have access to DefaultVendorSplits
// only check for DeploymentPermission here.
export const useHasDefaultVendorSplitPermission = () =>
  useHasDeploymentPermission(DeploymentPermissions.featureVendorSplits);

export const useHasDefaultBuyerSplitPermission = () =>
  useHasDeploymentPermission(DeploymentPermissions.featureBuyerSplits);

export const useHasGSTOverridePermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureGSTOverride,
    SaleyardPermissions.featureGSTOverride,
  );

export const useHasFinancialSaleSubTypesPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureFinancialSaleSubTypes,
    SaleyardPermissions.featureFinancialSaleSubTypes,
  );

export const useHasPaymentPreferenceForBusinessPermission = () =>
  useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.canAddPaymentPreferenceForBusiness,
    SaleyardPermissions.canAddPaymentPreferenceForBusiness,
  );
